import { useState } from 'react';
import { t } from '../lib/i18n';
import { classify, getAt, hasLabel, humanise, labelKeyFor, setAt } from '../lib/settingsSchema';
import type { JsonNode } from '../lib/settingsSchema';
import type { ReactNode } from 'react';
import { Card, CheckboxField, SelectField, TextField } from './ui';

interface Props {
  moduleKey: string;
  schema: Record<string, unknown>;
  values: Record<string, unknown>;
  defaults: Record<string, unknown>;
  disabled?: boolean;
  errors?: Record<string, string>;
  onChange: (next: Record<string, unknown>) => void;
  /** Shown at the bottom right of every section card (the save buttons). */
  footer?: ReactNode;
  /** Show only these top-level keys (default: all). */
  groups?: readonly string[];
  /** Hide these top-level keys. Applied after `groups`. */
  exceptGroups?: readonly string[];
}

function labelFor(moduleKey: string, path: string[]): string {
  return hasLabel(moduleKey, path) ? t(labelKeyFor(moduleKey, path)) : humanise(path[path.length - 1] ?? '');
}

function NumberField({
  label,
  hint,
  value,
  min,
  max,
  integer,
  disabled,
  error,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  min: number | undefined;
  max: number | undefined;
  integer: boolean;
  disabled: boolean | undefined;
  error: string | undefined;
  onChange: (n: number) => void;
}) {
  // Keep what the person is typing as text, and only report values that are numbers.
  const [text, setText] = useState(String(value));
  return (
    <TextField
      label={label}
      hint={hint}
      type="number"
      inputMode={integer ? 'numeric' : 'decimal'}
      step={integer ? 1 : 'any'}
      min={min}
      max={max}
      disabled={disabled}
      error={error}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        const n = Number(e.target.value);
        if (e.target.value.trim() !== '' && Number.isFinite(n)) onChange(n);
      }}
    />
  );
}

/**
 * A form built from the module's JSON Schema (SettingsResponse.jsonSchema).
 * Nested objects become groups; the label for each field is `settings.<module>.<path>`.
 */
export function SettingsForm({ moduleKey, schema, values, defaults, disabled, errors = {}, onChange, footer, groups, exceptGroups }: Props) {
  const set = (path: string[], v: unknown) => onChange(setAt(values, path, v));

  const renderNode = (node: JsonNode, path: string[]) => {
    const kind = classify(node);
    if (kind.kind === 'hidden') return null;
    const label = labelFor(moduleKey, path);
    const current = getAt(values, path);
    const def = getAt(defaults, path);
    const key = path.join('.');
    const error = errors[key];
    if (kind.kind === 'object') {
      return (
        <fieldset key={key} className="rounded-lg border border-slate-200 p-4">
          <legend className="px-1 text-sm font-semibold">{label}</legend>
          <div className="grid gap-4 md:grid-cols-2">{kind.properties.map(([n, child]) => renderNode(child, [...path, n]))}</div>
        </fieldset>
      );
    }
    const hint = def === undefined || def === '' ? undefined : t('common.default', { value: String(def) });
    switch (kind.kind) {
      case 'boolean':
        return (
          <CheckboxField
            key={key}
            className="md:col-span-2"
            label={label}
            checked={current === true}
            disabled={disabled}
            onChange={(e) => set(path, e.target.checked)}
          />
        );
      case 'number':
        return (
          <NumberField
            key={key}
            label={label}
            hint={hint ?? ''}
            value={typeof current === 'number' ? current : 0}
            min={kind.min}
            max={kind.max}
            integer={kind.integer}
            disabled={disabled}
            error={error}
            onChange={(n) => set(path, n)}
          />
        );
      case 'select':
        return (
          <SelectField key={key} label={label} value={String(current ?? '')} disabled={disabled} error={error} onChange={(e) => set(path, e.target.value)}>
            {kind.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </SelectField>
        );
      case 'colour':
        return (
          <div key={key} className="flex items-end gap-3">
            <TextField
              className="flex-1"
              label={label}
              hint={hint}
              value={String(current ?? '')}
              maxLength={7}
              disabled={disabled}
              error={error}
              onChange={(e) => set(path, e.target.value)}
            />
            <input
              type="color"
              aria-label={label}
              className="h-10 w-12 rounded border border-slate-300"
              disabled={disabled}
              value={/^#[0-9a-fA-F]{6}$/.test(String(current)) ? String(current) : '#000000'}
              onChange={(e) => set(path, e.target.value)}
            />
          </div>
        );
      case 'email':
        return (
          <TextField key={key} label={label} hint={hint} type="email" value={String(current ?? '')} disabled={disabled} error={error} onChange={(e) => set(path, e.target.value)} />
        );
      default:
        return (
          <TextField
            key={key}
            label={label}
            hint={hint}
            value={String(current ?? '')}
            maxLength={kind.kind === 'text' ? kind.maxLength : undefined}
            disabled={disabled}
            error={error}
            onChange={(e) => set(path, e.target.value)}
          />
        );
    }
  };

  const root = schema as JsonNode;
  const props = Object.entries(root.properties ?? {}).filter(([n]) => (!groups || groups.includes(n)) && !exceptGroups?.includes(n));
  // Each top-level group becomes its own card; loose top-level fields share one untitled card.
  const sections = props.filter(([, node]) => classify(node).kind === 'object');
  const loose = props.filter(([, node]) => classify(node).kind !== 'object');
  return (
    <div className="space-y-6">
      {loose.length > 0 ? (
        <Card footer={footer}>
          <div className="grid gap-4 md:grid-cols-2">{loose.map(([n, node]) => renderNode(node, [n]))}</div>
        </Card>
      ) : null}
      {sections.map(([n, node]) => {
        const kind = classify(node);
        if (kind.kind !== 'object') return null;
        return (
          <Card key={n} title={labelFor(moduleKey, [n])} footer={footer}>
            <div className="grid gap-4 md:grid-cols-2">{kind.properties.map(([cn, child]) => renderNode(child, [n, cn]))}</div>
          </Card>
        );
      })}
    </div>
  );
}
